// BILLSTMT1 (docs/build-briefs/BILLSTMT1-balances-statements.md; DEC-494, DEC-505, DEC-509; design/style-guide.md sections 5, 9,
// 10, 11, 12). The one Billing overview both consoles mount: what you owe Snip & Sip and, for a franchise location, your
// franchisor (balance, next debit date, bank last 4), a slot where the BANK1 Bank accounts card mounts, and the statement
// history with a Download PDF per row. The server does every calculation; this file draws its answer. All text goes in with
// textContent. A value the server does not know is left blank, never "N/A".

import { showBillingMode } from "./billingModeWords.ts";
import { exactDollars } from "./franchisorListModel.ts";
import { hoverHelp } from "./hoverHelp.ts";

export type StatementStatus = "paid" | "pending" | "failed" | "refunded";

export interface BalanceTileDto {
  balanceCents: number;
  statementMonth?: string | null;
  statementBalanceCents?: number | null;
  sinceStatementCents?: number | null;
  nextDebitDate: string | null;
  nextDebitCents?: number | null;
  methodLabel: string | null;
  name?: string;
}
export interface StatementRowDto {
  id: string;
  month: string;
  version: number;
  amountCents: number;
  balanceCents: number;
  status: StatementStatus;
}
export interface BillingOverviewDto {
  /** OWNLIVE1: the Stripe mode this customer is billed in. */
  billingMode?: unknown;
  snipsip: BalanceTileDto | null;
  franchisor: BalanceTileDto | null;
  statements: StatementRowDto[];
}

export const OVERVIEW_FAILED = "Your balances could not be loaded. Try again.";
export const DOWNLOAD_FAILED = "The statement could not be downloaded. Try again.";
export const NO_STATEMENTS = "No statements yet.";
export const NO_STATEMENTS_HELP = "Your first one arrives by email after the first full month.";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const isTile = (v: unknown): boolean =>
  isObject(v) && Number.isSafeInteger(v["balanceCents"]) && (v["nextDebitDate"] === null || typeof v["nextDebitDate"] === "string") &&
  (v["methodLabel"] === null || typeof v["methodLabel"] === "string");

export function isOverviewShape(value: unknown): value is BillingOverviewDto {
  if (!isObject(value) || !Array.isArray(value["statements"])) return false;
  if (value["snipsip"] !== null && !isTile(value["snipsip"])) return false;
  if (value["franchisor"] !== null && !isTile(value["franchisor"])) return false;
  return value["statements"].every(
    (row) =>
      isObject(row) && typeof row["id"] === "string" && typeof row["month"] === "string" && /^\d{4}-\d{2}$/.test(row["month"]) &&
      Number.isSafeInteger(row["version"]) && Number.isSafeInteger(row["amountCents"]) &&
      (row["status"] === "paid" || row["status"] === "pending" || row["status"] === "failed" || row["status"] === "refunded"),
  );
}

export function statementMonthText(month: string, version: number): string {
  const name = MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? "";
  return `${name} ${month.slice(0, 4)}${version > 1 ? " (corrected)" : ""}`;
}
export function monthName(month: string): string {
  return MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? "";
}
export function debitDateText(day: string): string {
  const [y, m, d] = day.split("-");
  return `${(MONTH_NAMES[Number(m) - 1] ?? "").slice(0, 3)} ${Number(d)}, ${y}`;
}
const STATUS_CHIP: Record<StatementStatus, { cls: string; symbol: string; word: string }> = {
  paid: { cls: "fl-chip-good", symbol: "✓", word: "Paid" },
  pending: { cls: "fl-chip-neutral", symbol: "", word: "Pending" },
  failed: { cls: "fl-chip-bad", symbol: "!", word: "Failed" },
  refunded: { cls: "fl-chip-info", symbol: "", word: "Refunded" },
};

export interface BillingStatementsDeps {
  /** Franchisor-only compact history presentation; other hosts retain their layout. */
  compactEmpty?: boolean;
  /** HQFRAN1 (B7): lead with the latest statement and next charge, and say in words when billing has nothing to show. */
  leadWithBill?: boolean;
  load: () => Promise<{ status: number; body: unknown }>;
  download: (id: string, fileName: string) => Promise<"ok" | "unauthorized" | "failed">;
  /** Opens the billing provider's page for the payment method; resolves with its address, or null when it could not. Omitted: no button. */
  openPaymentMethod?: () => Promise<string | null>;
  /** The load saw a 401: the caller ends the session. */
  sessionLost: () => void;
}
export interface BillingStatementsCard {
  root: HTMLElement;
  /** Where the BANK1 Bank accounts card mounts. */
  bankSlot: HTMLElement;
  load: () => Promise<void>;
  reset: () => void;
}

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string, testId?: string): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);
  if (className !== undefined) made.className = className;
  if (text !== undefined) made.textContent = text;
  if (testId !== undefined) made.setAttribute("data-testid", testId);
  return made;
}

export function createBillingStatementsCard(host: HTMLElement, deps: BillingStatementsDeps): BillingStatementsCard {
  const root = node("section", "bs-card", undefined, "billing-overview");
  const modeLine = node("p", "so-note bs-mode", undefined, "billing-mode");
  modeLine.setAttribute("role", "status");
  modeLine.hidden = true;
  const tiles = node("div", "bs-tiles", undefined, "billing-tiles");
  const bankSlot = node("div", "bs-bank-slot", undefined, "billing-bank-accounts-slot");
  bankSlot.id = "billing-bank-accounts-slot";
  const historyHead = node("h3", "bs-title", "Statements");
  hoverHelp(historyHead, "One statement per month, emailed after the month closes.", "Download PDF opens the same document that was emailed.");
  const history = node("div", "bs-history", undefined, "billing-statements");
  const status = node("p", "so-state", undefined, "billing-statements-status");
  status.setAttribute("role", "status");
  const payment = node("div", "bs-payment");
  payment.hidden = true;
  const manage = node("button", "btn-secondary", "Manage payment method", "billing-manage-payment");
  manage.type = "button";
  const paymentStatus = node("p", "field-error", undefined, "billing-payment-status");
  paymentStatus.setAttribute("role", "status");
  manage.addEventListener("click", () => {
    if (deps.openPaymentMethod === undefined) return;
    manage.disabled = true;
    paymentStatus.replaceChildren();
    void deps
      .openPaymentMethod()
      .then((url) => {
        if (url === null || !url.startsWith("https://")) {
          paymentStatus.textContent = "Stripe could not be opened. Try again.";
          return;
        }
        // The button itself opens Stripe; if the browser blocks the new tab, this tab goes there instead.
        const opened = window.open(url, "_blank");
        if (opened === null) window.location.assign(url);
        else opened.opener = null;
      })
      .finally(() => {
        manage.disabled = false;
      });
  });
  payment.append(manage, paymentStatus);
  root.append(modeLine, tiles, payment, bankSlot, historyHead, history, status);
  host.prepend(root);
  let generation = 0;

  const UNAVAILABLE_TITLE = "Your plan, statements and next charge are not shown yet.";
  const UNAVAILABLE_TEXT = "Billing is not set up for your network, so there is nothing to show here. Ask Snip & Sip if you expect a bill.";
  function unavailable(): HTMLElement {
    const box = node("div", "bs-unavailable", undefined, "billing-unavailable");
    box.setAttribute("role", "status");
    box.append(node("strong", undefined, UNAVAILABLE_TITLE), node("p", undefined, UNAVAILABLE_TEXT));
    return box;
  }

  function tile(label: string, data: BalanceTileDto, testId: string, latest?: StatementRowDto): HTMLElement {
    const card = node("div", "bs-tile", undefined, testId);
    const title = node("div", "bs-tile-label", label);
    card.append(title, node("div", "bs-tile-amount", exactDollars(data.balanceCents), `${testId}-amount`));
    if (data.statementMonth != null && data.statementBalanceCents != null && data.sinceStatementCents != null && data.sinceStatementCents !== 0) {
      card.append(
        node(
          "div",
          "bs-tile-since",
          `${exactDollars(data.statementBalanceCents)} on your ${monthName(data.statementMonth)} statement, plus ${exactDollars(data.sinceStatementCents)} since`,
          `${testId}-since`,
        ),
      );
    }
    const lines = node("dl", "bs-tile-facts");
    const fact = (name: string, value: string, id: string): void => {
      const group = node("div", "bs-tile-fact");
      group.append(node("dt", undefined, name), node("dd", undefined, value, id));
      lines.append(group);
    };
    if (deps.leadWithBill === true && testId === "billing-tile-snipsip") {
      fact(
        "Latest statement",
        latest === undefined ? "None yet. The first arrives after the first full month." : `${statementMonthText(latest.month, latest.version)}: ${exactDollars(latest.amountCents)}, ${STATUS_CHIP[latest.status].word}`,
        `${testId}-latest`,
      );
      if (data.nextDebitDate === null) fact("Next charge", "Not scheduled", `${testId}-next`);
    }
    if (data.nextDebitDate !== null)
      fact(deps.leadWithBill === true ? "Next charge" : "Next debit", `${data.nextDebitCents == null ? "" : `${exactDollars(data.nextDebitCents)} on `}${debitDateText(data.nextDebitDate)}`, `${testId}-next`);
    if (data.methodLabel !== null) fact("From", data.methodLabel, `${testId}-bank`);
    if (lines.childElementCount > 0) card.append(lines);
    return card;
  }

  function draw(data: BillingOverviewDto): void {
    showBillingMode(modeLine, data.billingMode);
    tiles.replaceChildren();
    if (data.snipsip !== null) tiles.append(tile("Owed to Snip & Sip", data.snipsip, "billing-tile-snipsip", data.statements[0]));
    if (data.franchisor !== null)
      tiles.append(tile(`Owed to ${data.franchisor.name ?? "your franchisor"}`, data.franchisor, "billing-tile-franchisor"));
    historyHead.hidden = false;
    if (deps.leadWithBill === true && data.snipsip === null) tiles.prepend(unavailable());
    tiles.hidden = tiles.childElementCount === 0;
    payment.hidden = data.snipsip === null || deps.openPaymentMethod === undefined;
    // The button sits inside the Snip & Sip tile.
    if (!payment.hidden) tiles.querySelector("[data-testid=billing-tile-snipsip]")?.append(payment);
    history.replaceChildren();
    if (data.statements.length === 0) {
      const empty = node("div", deps.compactEmpty ? "bs-empty-compact" : "fl-empty");
      empty.append(node("p", undefined, NO_STATEMENTS), node("p", "fb-note", NO_STATEMENTS_HELP));
      history.append(empty);
      return;
    }
    const table = node("table", "fr-table bs-table", undefined, "billing-statements-table");
    const head = document.createElement("thead");
    const headRow = document.createElement("tr");
    ["Month", "Amount", "Status", ""].forEach((label, index) => {
      const th = document.createElement("th");
      th.scope = "col";
      th.textContent = label;
      if (index === 1) th.className = "fb-num";
      if (index === 3) th.className = "fb-num";
      headRow.append(th);
    });
    head.append(headRow);
    const body = document.createElement("tbody");
    for (const row of data.statements) {
      const tr = document.createElement("tr");
      tr.setAttribute("data-testid", `billing-statement-${row.id}`);
      const month = document.createElement("td");
      month.className = "fb-date";
      month.textContent = statementMonthText(row.month, row.version);
      const amount = document.createElement("td");
      amount.className = "fb-num";
      amount.textContent = exactDollars(row.amountCents);
      const state = document.createElement("td");
      const chip = STATUS_CHIP[row.status];
      const pill = node("span", `fl-chip ${chip.cls}`);
      if (chip.symbol !== "") pill.append(node("span", "fl-chip-symbol", chip.symbol), document.createTextNode(" "));
      pill.append(document.createTextNode(chip.word));
      state.append(pill);
      const action = document.createElement("td");
      action.className = "fb-num";
      const button = node("button", "btn-secondary btn-compact", "Download PDF", "billing-statement-download");
      button.type = "button";
      button.addEventListener("click", () => {
        button.disabled = true;
        status.textContent = "";
        void deps
          .download(row.id, `snip-and-sip-statement-${row.month}${row.version > 1 ? `-v${row.version}` : ""}.pdf`)
          .then((outcome) => {
            if (outcome === "unauthorized") deps.sessionLost();
            else if (outcome === "failed") status.textContent = DOWNLOAD_FAILED;
          })
          .finally(() => {
            button.disabled = false;
          });
      });
      action.append(button);
      tr.append(month, amount, state, action);
      body.append(tr);
    }
    table.append(head, body);
    const scroll = node("div", "fr-table-scroll");
    scroll.append(table);
    history.append(scroll);
  }

  async function load(): Promise<void> {
    const expected = ++generation;
    status.textContent = "Loading...";
    const reply = await deps.load();
    if (expected !== generation) return;
    if (reply.status === 401) {
      deps.sessionLost();
      return;
    }
    if (reply.status === 404) {
      status.textContent = "";
      if (deps.leadWithBill === true) {
        root.hidden = false;
        history.replaceChildren();
        historyHead.hidden = true;
        tiles.replaceChildren(unavailable());
        tiles.hidden = false;
        return;
      }
      root.hidden = true;
      return;
    }
    root.hidden = false;
    if (reply.status !== 200 || !isOverviewShape(reply.body)) {
      status.textContent = "";
      history.replaceChildren();
      tiles.hidden = true;
      const text = node("p", "field-error", OVERVIEW_FAILED);
      text.setAttribute("role", "alert");
      const retry = node("button", "btn-secondary", "Try again");
      retry.type = "button";
      retry.addEventListener("click", () => void load());
      history.append(text, retry);
      return;
    }
    status.textContent = "";
    draw(reply.body);
  }

  function reset(): void {
    generation += 1;
    tiles.replaceChildren();
    history.replaceChildren();
    status.textContent = "";
    root.hidden = true;
  }

  reset();
  return { root, bankSlot, load, reset };
}
